import axios, {type AxiosRequestConfig} from "axios";
import pako from "pako";
import Translator from "@/utils/common/Translator";
import {useErrorsStore} from "@/stores/ToastDataStore";
import LoginUtils from "@/utils/login/LoginUtils.ts";
import MetaData from "@/utils/common/MetaData.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import {useLoadingStore} from "@/stores/LoadingStore.ts";
import SecurityUtils from "@/utils/login/SecurityUtils.ts";
import EntitiesWithLedgerEffectsOrWithParents from "@/utils/constants/EntitiesWithLedgerEffectsOrWithParents.ts";
import NaMaErrors from "@/utils/constants/NaMaErrors.ts";
import CommonFieldIds from "@/utils/constants/CommonFieldIds.ts";
import EntityTypesUtils from "@/utils/common/EntityTypesUtils.ts";
import NaMaMetaData = namaTypes.NaMaMetaData;
import GCFailure = namaTypes.GCFailure;
import DTOGui2Response = namaTypes.DTOGui2Response;
import QuestionField = namaTypes.QuestionField;
import BasicFieldMetaData = namaTypes.BasicFieldMetaData;
import FieldMetaData = namaTypes.FieldMetaData;

export const NO_DATE: Date = new Date(1970, 1, 1);

// Compression settings - compress payloads larger than 50KB
const COMPRESSION_THRESHOLD = 50 * 1024;

export interface NamaAxiosRequestConfig extends AxiosRequestConfig {
  noLoadingIndicator?: boolean,
  doNotPushErrorWhenFailure?: boolean,
  doNotUseMainReqForHttp?: boolean,
}

export default class NamaCommon {
  static async namaFetch(url: string, settings: NamaAxiosRequestConfig) {
    const errorsStore = useErrorsStore();
    settings.params = settings.params || {};
    settings.params.trackLogs = useNamaFlagsStore().serverLogActive;
    settings.params.vue = true;
    this.addCommonParametersToRequest(settings.params);
    if (settings.params.entityType) {
      settings.params.viewOnly = !(SecurityUtils.userCanCreate(settings.params.entityType) || SecurityUtils.userCanEdit(settings.params.entityType));
    }
    settings.headers = settings.headers ?? {};
    settings.headers["content-Type"] = "application/json";
    // Compress large payloads with gzip
    if (settings.data) {
      const jsonData = typeof settings.data === "string" ? settings.data : JSON.stringify(settings.data);
      if (jsonData.length > COMPRESSION_THRESHOLD && !url.includes("/spr/")) {
        settings.data = pako.gzip(jsonData);
        settings.headers["Content-Encoding"] = "gzip";
      }
    }
    settings.method = settings.method ? settings.method : 'post';
    if (!settings.noLoadingIndicator)
      useLoadingStore().startLoading();
    try {
      const response = await axios(url, settings);
      if (response && response.data && response.data.errors && response.data.errors.length) {
        if (this.isAuthenticationError(response)) {
          let loggedIn = await LoginUtils.tryLoginFromSessionOrLogout();
          if (loggedIn)
            return await this.namaFetch(url, settings);
        }
        if (response.data)
          errorsStore.updateLastLog(response.data.log);
        if (!settings.doNotPushErrorWhenFailure) {
          if (!response.data.confirmations?.length)
            errorsStore.pushErrors(response.data.errors as namaTypes.NaMaError[]);
        }
        return response.data;
      }
      if (response.status >= 200 && response.status < 300) {
        return response.data;
      } else {
        useErrorsStore().pushErrorStr(`Request failed with status ${response.status}`);
        throw new Error(`Request failed with status ${response.status}`);
      }
    } catch (error: any) {
      if (!settings.doNotPushErrorWhenFailure) {
        const status = error?.response?.status ?? '';
        const message = error?.response?.data?.message ?? error?.message ?? error;
        useErrorsStore().pushErrorStr(`Request failed${status ? ' (' + status + ')' : ''}: ${message}`);
      }
      throw error;
    } finally {
      if (!settings.noLoadingIndicator)
        useLoadingStore().stopLoading();
    }
  }

  private static isAuthenticationError(response: any): boolean {
    if (response.data.errorType)
      return response.data.errorType === NaMaErrors.AUTHENTICATION_ERROR;
    return false;
  }

  static namaCompare(o1: any = null, o2: any = null): number {
    if (o1 === o2)
      return 0;
    if (o1 > o2)
      return 1;
    if (o1 < o2)
      return -1;
    if (o1 === null)
      return -1;
    if (o2 == null)
      return 1;
    if (o1.id || o2.id)
      return this.namaCompare(o1.id, o2.id)
    return this.namaCompare(o1.toString(), o2.toString());
  }

  static getDateDifferenceAsString(dateString: Date): string {
    const targetDate = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - targetDate.getTime();

    const diffInSeconds = Math.floor(diffInMs / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    const hours = diffInHours % 24;
    const minutes = diffInMinutes % 60;
    const seconds = diffInSeconds % 60;

    if (diffInDays)
      return diffInDays + ' ' + Translator.translateId("timeDay");
    if (hours)
      return hours + ' ' + Translator.translateId("Hour");
    if (minutes)
      return minutes + ' ' + Translator.translateId("Minute");
    if (seconds)
      return '1 ' + Translator.translateId("Minute");
    return "";
  }

  static defaultCompare<T>(a: T, b: T): number {
    if (a < b) return -1;
    if (a > b) return 1;
    return 0;
  }

  static binarySearch<T>(arr: T[], el: T, compareFn?: (a: T, b: T) => number): number {
    let left = 0;
    let right = arr.length - 1;
    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const cmp = (compareFn ?? this.defaultCompare)(arr[mid], el);
      if (cmp === 0)
        return mid;
      else if (cmp < 0)
        left = mid + 1;
      else
        right = mid - 1;
    }
    return -1;
  }

  static getCookie(name: string) {
    const value: string = `; ${document.cookie}`;
    const parts: string[] | undefined = value.split(`; ${name}=`);
    if (parts && parts.length === 2) return parts.pop()!.split(';').shift();
  }

  static substringBefore(str: string, separator: string) {
    if (!str || !separator) {
      return str;
    }
    if (!separator.length) {
      return "";
    }
    let pos = str.indexOf(separator);
    if (pos == -1) {
      return str;
    }
    return str.substring(0, pos);
  }

  static substringAfter(str: string, separator: string) {
    if (!str || !separator) {
      return str;
    }
    if (!separator.length) {
      return "";
    }
    let pos = str.indexOf(separator);
    if (pos == -1) {
      return "";
    }
    return str.substring(pos + separator.length);
  }

  static addCommonParametersToRequest(request: Record<string, any>) {
    if (request && LoginUtils.loginInfo) {
      if (!request.uId)
        request.uId = LoginUtils.currentUserId();
      if (!request.lId && LoginUtils.loginInfo && LoginUtils.loginInfo.userInfo)
        request.lId = LoginUtils.loginInfo.userInfo.loginId;
      if (useNamaFlagsStore().inChangeDimsDialog) {
        request.leg = LoginUtils.loginInfo.userInfo?.legalEntity?.id;
        request.sc = LoginUtils.loginInfo.userInfo?.sector?.id;
        request.br = LoginUtils.loginInfo.userInfo?.branch?.id;
        request.dep = LoginUtils.loginInfo.userInfo?.department?.id;
        request.an = LoginUtils.loginInfo.userInfo?.analysisSet?.id;
      } else {
        request.leg = LoginUtils.loginInfo.dimensions?.legalEntity ? LoginUtils.loginInfo.dimensions.legalEntity.id : null;
        request.sc = LoginUtils.loginInfo.dimensions?.sector ? LoginUtils.loginInfo.dimensions.sector.id : null;
        request.br = LoginUtils.loginInfo.dimensions?.branch ? LoginUtils.loginInfo.dimensions.branch.id : null;
        request.dep = LoginUtils.loginInfo.dimensions?.department ? LoginUtils.loginInfo.dimensions.department.id : null;
        request.an = LoginUtils.loginInfo.dimensions?.analysisSet ? LoginUtils.loginInfo.dimensions.analysisSet.id : null;
      }
    }
    request.locale = Translator.locale;
    request.trackLogs = useNamaFlagsStore().serverLogActive || "";
  }

  static stringify(value: any): string {
    let s = JSON.stringify(value);
    return s ?? "";
  }

  static getFieldValue(object: Record<string, any> | undefined, path: string, row?: number): any {
    if (!object)
      return undefined;
    let parts = path.split('.');
    let current = object;
    for (const item of parts) {
      current = current[item];
      if ((row || row === 0) && row !== -1 && Array.isArray(current))
        current = current[row];
      if (!current)
        return current;
    }
    return current;
  }

  static setFieldValue(object: Record<string, any> | undefined, path: string, value: any, row?: number | null) {
    if (!object) {
      console.log(object, path, value, row, new Error("can not set field value if root is null or undefined"))
      return;
    }
    let parts = path.split('.');
    let current = object as Record<string, any>;
    for (let i = 0; i < parts.length - 1; i++) {
      let e = current[parts[i]];
      if (!e)
        e = current[parts[i]] = {};
      if (Array.isArray(e) && (row || row === 0) && row > -1) {
        if (row >= e.length) {
          console.log("Error: row " + row + " is greater than array size " + e.length + ", field id is " + path);
          return;
        }
        current = e[row];
      }
      else
        current = e;
    }
    current[parts[parts.length - 1]] = value;
  }

  static listNonBlankDetailLines(lines: any): any[] {
    if (!Array.isArray(lines))
      return [];
    return lines.filter(line => !this.isBlankDetailLine(line));
  }

  static isBlankDetailLine(line: any): boolean {
    if (ObjectChecker.isNullOrUndefined(line))
      return true;
    for (let key of Object.keys(line)) {
      if (key === "id")
        continue;
      if (!this.isBlankLineValue(line[key]))
        return false;
    }
    return true;
  }

  static isBlankLineValue(value: any): boolean {
    if (value === null || value === undefined || value === "")
      return true;
    if (ObjectChecker.isArray(value))
      return value.length === 0;
    if (ObjectChecker.isObject(value))
      return Object.keys(value).length === 0;
    return false;
  }

  static areTwoObjectsEqual(o1: Record<string, any> | null | undefined, o2: Record<string, any> | null | undefined, metadata: NaMaMetaData, ignoreFields?: string[]) {
    if (!o1 || !o2)
      return true;
    MetaData.initDetailsMetaData(metadata)
    ignoreFields?.sort()
    for (let field of metadata.fieldsMetaData ?? []) {
      if (!field.fieldId)
        continue;
      if (field.detail)
        continue;
      if (field.guiField)
        continue;
      if (field.systemField)
        continue;
      if (ignoreFields?.binaryContains(field.fieldId))
        continue;
      if (field['parentDetail'])
        continue;
      let o1v = this.getFieldValue(o1, field.fieldId);
      let o2v = this.getFieldValue(o2, field.fieldId);
      if (!this.checkEqual(o1v, o2v, field)) {
        if (this.shouldLog())
          console.log("changed field", field, o1v, o2v)
        return false;
      }
    }
    let detailFields = metadata["detailsMetaData"];
    for (let [detailId, detailField] of detailFields.entries()) {
      let rawO1lines = this.getFieldValue(o1, detailField.fieldId)
      let rawO2lines = this.getFieldValue(o2, detailField.fieldId)
      if (rawO1lines === rawO2lines)
        continue;
      let o1lines = this.listNonBlankDetailLines(rawO1lines)
      let o2lines = this.listNonBlankDetailLines(rawO2lines)
      if (o1lines.length === 0 && o2lines.length === 0)
        continue;
      if (o1lines.length !== o2lines.length) {
        if (this.shouldLog())
          console.log("changed field length", detailField.fieldId, o1lines, o2lines)
        return false;
      }
      let subFields: FieldMetaData[] = detailField.subFields || metadata.fieldsMetaData?.filter(l => l.fieldId?.startsWith(detailField.fieldId + ".")) || [detailField];
      subFields.push({
        fieldId: "criteria.expressions.rightHandSide",
        fieldType: "Text",
        id: "criteria.expressions.rightHandSide",
        fieldInformation: {
          fieldId: "criteria.expressions.rightHandSide"
        }
      }, {
        fieldId: "criteria.expressions.inValues",
        fieldType: "Detail",
        id: "criteria.expressions.inValues",
        fieldInformation: {
          fieldId: "criteria.expressions.inValues"
        }
      });
      for (let subField of subFields) {
        if (subField.detail)
          continue;
        if (subField.guiField)
          continue;
        if (subField.systemField)
          continue;
        if (!subField.fieldId)
          continue;
        if (ignoreFields?.binaryContains(subField.fieldId))
          continue;
        let trimmedFieldId = subField.fieldId.substring(detailField.fieldId.length + 1)
        for (let i = 0; i < o1lines.length; i++) {
          let o1Line = o1lines[i], o2line = o2lines[i];
          let o1v = this.getFieldValue(o1Line, trimmedFieldId);
          let o2v = this.getFieldValue(o2line, trimmedFieldId);
          if (!this.checkEqual(o1v, o2v, subField) && "criteria.expressions.id" !== subField.fieldId) {
            if (this.shouldLog())
              console.log("changed field", subField, o1v, o2v)
            return false;
          }
        }
      }
    }
    return true;
  }

  static shouldLog() {
    return (window as any).shouldLog;
  }

  static deepEqual(obj1: any, obj2: any, ...skipKeys: string[]): boolean {
    if (obj1 === obj2) {
      return true;
    }
    if (typeof obj1 !== 'object' || obj1 === null || typeof obj2 !== 'object' || obj2 === null) {
      return false;
    }
    let keys1 = Object.keys(obj1);
    let keys2 = Object.keys(obj2);
    if (skipKeys.length) {
      keys1 = keys1.filter(e => !skipKeys.includes(e));
      keys2 = keys2.filter(e => !skipKeys.includes(e));
    }
    if (keys1.length !== keys2.length) {
      return false;
    }
    for (const key of keys1) {
      if (!keys2.includes(key) || !this.deepEqual(obj1[key], obj2[key], ...skipKeys)) {
        return false;
      }
    }
    return true;
  }

  static checkEqual(o1: Record<string, any>, o2: Record<string, any>, fieldMetadata: namaTypes.FieldMetaData) {
    if (!o1 && !o2)
      return true;
    if (ObjectChecker.areEqual(o1, o2))
      return true;
    if (fieldMetadata.fieldType === FieldType.Binary) {
      let o1info = o1 ? o1.attachmentInfo : null;
      let o2info = o2 ? o2.attachmentInfo : null;
      o1info = o1info || {};
      o2info = o2info || {};
      return (o1info).fileId === o2info.fileId && o1info.updated === o2info.updated && o1info.removed === o2info.removed;
    }
    return false;
  }

  static calcTopAndBottomPaddings(element: any): number {
    if (!element)
      return 0;
    let cs = window.getComputedStyle(element);
    return parseInt(cs.getPropertyValue("padding-top")) + parseInt(cs.getPropertyValue("padding-bottom"));
  }

  static parseOperators(textParam: string) {
    let text = textParam.toString();
    if (text.indexOf('^') < 0 && text.indexOf('*') < 0 && text.indexOf('/') < 0 && text.indexOf('+') < 0 && text.indexOf('-') < 0)
      return null;
    return eval(text.replace(",", ""));
  }

  static isSuccess<T extends any>(response: T | DTOGui2Response | GCFailure): response is T {
    if ((response as GCFailure).failureOccurred)
      return false;
    if ((response as DTOGui2Response).result && (response as DTOGui2Response).result?.failureOccurred)
      return false;
    return true;
  }

  static extractErrorsFromResponse(response: namaTypes.DTOGui2Response | namaTypes.GCFailure | undefined) {
    if ((response as GCFailure).errors)
      return (response as GCFailure).errors;
    if ((response as DTOGui2Response).result && (response as DTOGui2Response).result?.errors)
      return (response as DTOGui2Response).result?.errors;
    return [];
  }

  static cloneObject(result: Record<string, any>) {
    return JSON.parse(JSON.stringify(result));
  }

  static createQuestionField(fieldId: string, fieldType: string, layout?: string, richText?: boolean, required?: boolean) {
    let questionField: QuestionField = {id: fieldId};
    let metaData: BasicFieldMetaData = {id: fieldId};
    questionField.layout = layout;
    questionField.required = required;
    metaData.fieldType = fieldType;
    metaData.richText = richText;
    metaData.fieldId = fieldId;
    questionField.metaData = metaData;
    return questionField;
  }

  static createQuestionFieldWithAllowedValues(fieldId: string, fieldType: string, allowedValues: string[], layout?: string, required?: boolean) {
    let questionField: QuestionField = {id: fieldId};
    let metaData: BasicFieldMetaData = {id: fieldId};
    questionField.layout = layout;
    questionField.required = required;
    metaData.fieldType = fieldType;
    metaData.allowedValues = allowedValues;
    metaData.fieldId = fieldId;
    questionField.metaData = metaData;
    return questionField;
  }

  static createQuestionFieldRef(fieldId: string, referenceEntityType: string | undefined, layout?: string, required?: boolean) {
    let questionField: QuestionField = {id: fieldId};
    let metaData: BasicFieldMetaData = {id: fieldId};
    questionField.layout = layout;
    questionField.required = required;
    metaData.fieldType = FieldType.Reference;
    metaData.referencedEntityType = referenceEntityType;
    metaData.fieldId = fieldId;
    questionField.metaData = metaData;
    return questionField;
  }

  static hasParentEntity(entityType: string): boolean {
    return EntitiesWithLedgerEffectsOrWithParents.HAS_PARENT_ENTITIES.contains(entityType);
  }

  static getBooleanFromStorageOrDefault(itemId: string, defaultValue: boolean): boolean {
    const storedValue = localStorage.getItem(itemId);
    if (!storedValue)
      return defaultValue;
    return storedValue === "true";
  }

  static getStoredValue<T>(key: string, defaultValue: any): T | any {
    const storedValue = localStorage.getItem(key) as string | null;
    if (!storedValue)
      return defaultValue;
    return JSON.parse(storedValue) as T;
  }

  static setInStorage(key: string, value: any) {
    localStorage.setItem(key, this.stringify(value));
  }

  static nameOfRef(ref?: Record<any, any>) {
    let text: string;
    if (!ref || !ref[CommonFieldIds.ID])
      return '';
    if (EntityTypesUtils.isMaster(ref.entityType)) {
      let name = Translator.getName(ref[CommonFieldIds.NAME1], ref[CommonFieldIds.NAME2]);
      if (name)
        text = name;
      else
        text = Translator.getName(ref[CommonFieldIds.CODE], ref[CommonFieldIds.ALT_CODE])!;
    } else {
      text = Translator.getName(ref[CommonFieldIds.CODE], ref[CommonFieldIds.ALT_CODE])!;
    }
    return text;
  }

  static formatToK(value?: number) {
    if (!value)
      return
    else if (value < 1000 && value > -1000)
      return value;
    else
      return (value / 1000).toFixed(0) + "K"
  }

  static async waitFor(ms: number = 0) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  static sideBarMenuModes = Object.freeze({
    FOCUSED_TREE_MODE: "FocusedTreeMode",
    DRILL_DOWN_MODE: "DrillDownMode",
    TREE_MODE: "TreeMode"
  })

  static getSideBarMenuModeOptions() {
    const values = Object.values(NamaCommon.sideBarMenuModes);
    return values.map(o => ({label: Translator.translateId(o), value: o}))
  }
}
